import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import type { HostedAgentEvalCaseInput, HostedAgentEvalRun } from "@/api/admin-api"

import {
  createHostedAgentEvalCase,
  deleteHostedAgentEvalCase,
  getHostedAgentEvalRun,
  listHostedAgentEvalCases,
  listHostedAgentEvalRuns,
  startHostedAgentEvalRun,
  updateHostedAgentEvalCase,
} from "@/api/admin-api"

import { FAST_STALE_TIME, GC_TIME } from "./_shared"
import {
  HOSTED_AGENT_EVAL_POLL_FAILURE_LIMIT,
  HOSTED_AGENT_EVAL_POLL_MS,
  planHostedAgentEvalPoll,
} from "../../utils/hosted-agent-eval-run-state"

// Eval suites (#3028). Two shapes of data with two very different cadences:
// the exam (cases) is ordinary CRUD, while a suite RUN is started with a 202
// and then polled — POST returns a `running` document and the turns happen in
// a background task, so the run document is the only progress signal.
//
// Keys live under the hosted-agents subtree so any agent-level invalidation
// sweeps them too (the admin domain does not use lib/query-keys.ts).
const HOSTED_AGENTS_KEY = ["admin", "hosted-agents"] as const

/** Cases for one agent — the persistent exam, edited without running anything. */
export function useAdminHostedAgentEvalCasesQuery(agentId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: [...HOSTED_AGENTS_KEY, agentId, "eval-cases"],
    queryFn: () => listHostedAgentEvalCases(agentId ?? ""),
    enabled: enabled && !!agentId,
    staleTime: FAST_STALE_TIME,
    gcTime: GC_TIME,
  })
}

/**
 * Recent runs, newest first. Polls while any listed run is still `running` so
 * the history row flips to its verdict without a manual refresh.
 */
export function useAdminHostedAgentEvalRunsQuery(
  agentId: string | undefined,
  enabled = true,
  limit = 20
) {
  return useQuery({
    queryKey: [...HOSTED_AGENTS_KEY, agentId, "eval-runs", limit],
    queryFn: () => listHostedAgentEvalRuns(agentId ?? "", limit),
    enabled: enabled && !!agentId,
    staleTime: 0,
    gcTime: GC_TIME,
    refetchInterval: (query) => {
      // A list that cannot be read stops polling too — an unreachable endpoint
      // must not be retried every 3 s until the dialog closes.
      if (query.state.fetchFailureCount >= HOSTED_AGENT_EVAL_POLL_FAILURE_LIMIT) return false
      const runs = query.state.data as HostedAgentEvalRun[] | undefined
      return runs?.some((run) => run.status === "running") ? HOSTED_AGENT_EVAL_POLL_MS : false
    },
  })
}

/**
 * One run, polled while it is `running` — the canonical repo pattern
 * (HOSTED_AGENT_LIFECYCLE_POLL_MS). `case_results` grows incrementally, so each
 * poll can draw one more case; when the suite dies halfway the half-filled
 * answer sheet is still readable.
 *
 * The stop condition is NOT just "status became terminal". The 202 document is
 * seeded straight into this cache entry, so a GET that then 404s or times out
 * leaves a permanently `running` document behind: `planHostedAgentEvalPoll`
 * also gives up after repeated read failures and after the run has outlived
 * any plausible suite, and the panel renders both of those states.
 */
export function useAdminHostedAgentEvalRunQuery(
  agentId: string | undefined,
  runId: string | undefined,
  enabled = true
) {
  return useQuery({
    queryKey: [...HOSTED_AGENTS_KEY, agentId, "eval-runs", runId],
    queryFn: () => getHostedAgentEvalRun(agentId ?? "", runId ?? ""),
    enabled: enabled && !!agentId && !!runId,
    staleTime: 0,
    gcTime: GC_TIME,
    // One retry per poll: the failure count has to mean "reads keep failing",
    // not "the default three-retry ladder is still climbing".
    retry: 1,
    refetchInterval: (query) =>
      planHostedAgentEvalPoll(
        query.state.data as HostedAgentEvalRun | undefined,
        query.state.fetchFailureCount
      ).intervalMs,
  })
}

function useInvalidateEvalCases() {
  const queryClient = useQueryClient()
  return async (agentId: string) => {
    await queryClient.invalidateQueries({
      queryKey: [...HOSTED_AGENTS_KEY, agentId, "eval-cases"],
    })
  }
}

export function useCreateHostedAgentEvalCaseMutation() {
  const invalidate = useInvalidateEvalCases()
  return useMutation({
    mutationFn: ({ agentId, input }: { agentId: string; input: HostedAgentEvalCaseInput }) =>
      createHostedAgentEvalCase(agentId, input),
    onSuccess: (_case, { agentId }) => invalidate(agentId),
  })
}

export function useUpdateHostedAgentEvalCaseMutation() {
  const invalidate = useInvalidateEvalCases()
  return useMutation({
    mutationFn: ({
      agentId,
      caseId,
      input,
    }: {
      agentId: string
      caseId: string
      input: HostedAgentEvalCaseInput
    }) => updateHostedAgentEvalCase(agentId, caseId, input),
    onSuccess: (_case, { agentId }) => invalidate(agentId),
  })
}

export function useDeleteHostedAgentEvalCaseMutation() {
  const invalidate = useInvalidateEvalCases()
  return useMutation({
    mutationFn: ({ agentId, caseId }: { agentId: string; caseId: string }) =>
      deleteHostedAgentEvalCase(agentId, caseId),
    onSuccess: (_caseId, { agentId }) => invalidate(agentId),
  })
}

/**
 * Start the suite (202). The mutation resolves with the `running` document;
 * the caller keeps its id and lets the polling query drive the rest. The run
 * list is invalidated immediately so the new run appears in the history at
 * once, still marked as running.
 */
export function useStartHostedAgentEvalRunMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ agentId, caseIds }: { agentId: string; caseIds?: string[] }) =>
      startHostedAgentEvalRun(agentId, caseIds),
    onSuccess: async (run, { agentId }) => {
      queryClient.setQueryData([...HOSTED_AGENTS_KEY, agentId, "eval-runs", run.id], run)
      await queryClient.invalidateQueries({
        queryKey: [...HOSTED_AGENTS_KEY, agentId, "eval-runs"],
      })
    },
  })
}
